//TODO: Skipping tests for now. Add test coverage and remove this line in LEARN-11019
/* istanbul ignore file */
import { useEffect, useState } from 'react'

import { QueryType } from '@cfc/content-service-common'
import ContentServiceClient from '@cfc/content-service-sdk'
import { backoff } from '@cfc/exponential-backoff'
import {
  ENTRY_ID_PARAM_ERROR,
  LOAD_PRESENTATION_ERROR,
  MOCK_DATA,
  PREVIEW_PARAM_ERROR,
  SUCCESS,
} from 'components/AppWrapper/constants'
import hydrate from 'lib/APIConnector/hydrate'
import rehydrateRedux from 'lib/APIConnector/rehydrateRedux'
import { IS_CONNECTED_TO_API, SUBDOMAIN } from 'lib/config'
import { hydrateCmi, updateCmiLocale } from 'lib/state/actions/cmi'
import {
  addTheme,
  initializePlayer,
  setCurriculumPreviewMode,
  setFamilyMode,
  setHighSchoolMode,
  setHighSchoolPreviewMode,
  setVtts,
  togglePrepMode,
} from 'lib/state/actions/player'
import configureStore from 'lib/state/store'
import { getLocationData, getParams, handleAuthUpdate } from 'lib/url'
import { getUserContext } from 'lib/UserContext/getUserContext'
import { getBoolFromString } from 'utils/boolHelper'
import { mapContentServiceToContentfulResponse } from 'utils/contentServiceHelper'
import { getLocaleFromSession, LOCALES } from 'utils/locale'
import { getPlayerSessionTimeManager } from 'utils/sessionHelper'

// NOTE: fetching user context is disabled for now, because the player's content is not audienced so this import is not needed:
// import { useUserContext } from '../lib/UserContext/provider'

export const useInitAuth = auth0 => {
  const getTokenFromUrl = () =>
    new URLSearchParams(window.location.search).get('token')
  const token = getTokenFromUrl()
  if (!token) {
    // If no token is found, proceed with the auth flow
    handleAuthUpdate(auth0)
  }
  return token
}

export const useInitApp = fetchedToken => {
  const [initialStore, setInitialStore] = useState()
  const [error, setError] = useState()
  const [urlToken, setUrlToken] = useState(null)

  // NOTE: fetching user context is disabled for now, because the player's content is not audienced so this call is not needed:
  // const { data: userContext, error: userContextError } = useUserContext()

  useEffect(() => {
    const tokenFromUrl = new URLSearchParams(window.location.search).get(
      'token',
    )
    setUrlToken(tokenFromUrl)
  }, [])

  const lmsMode = window.parent.__CFC_PLAYER_DATA__

  useEffect(() => {
    if (fetchedToken || urlToken || lmsMode) {
      prepareStore(fetchedToken || urlToken)
    }
  }, [fetchedToken, urlToken])

  const prepareStore = async () => {
    let initialStore = configureStore()

    await hydrateStore(initialStore)
    initialStore = await populateStoreWithParams(initialStore)

    setInitialStore(initialStore)
  }

  const hydrateStore = async store => {
    const hydratedData = await hydrate()
    // Hydrate the store with data from the api and feature flags
    hydratedData && (await store.dispatch(hydrateCmi(hydratedData)))
  }

  const loadPresentationJson = async (
    contentfulId,
    preview,
    locale,
    contentReview,
  ) => {
    const client = new ContentServiceClient({
      environment:
        SUBDOMAIN && SUBDOMAIN.length > 0
          ? SUBDOMAIN.startsWith('-')
            ? SUBDOMAIN.substring(1)
            : SUBDOMAIN
          : 'p-prod',
    })
    const userContext = getUserContext()

    // Build common args for both getContent and getOriginContent
    const contentArgs = {
      entryId: contentfulId,
      queryType: QueryType.presentation,
      isPreview: preview,
      locale: locale,
    }
    if (userContext) {
      contentArgs.userContext = userContext
    }

    let data
    if (contentReview) {
      // Use getOriginContent for content review (unreleased/draft) version
      // Check if getOriginContent method is available
      if (typeof client.getOriginContent === 'function') {
        data = await backoff(() => client.getOriginContent(contentArgs))
      } else {
        console.warn(
          'getOriginContent method not available in current SDK version. Falling back to getContent.',
        )
        data = await backoff(() => client.getContent(contentArgs))
      }
    } else {
      // Use getContent for regular content
      data = await backoff(() => client.getContent(contentArgs))
    }

    return {
      data: {
        sys: {
          // fill this in since the old Contentful SDK would return it:
          id: contentfulId,
        },
        ...mapContentServiceToContentfulResponse(data),
      },
      // if content-service did not error out, assume success:
      status: SUCCESS,
    }
  }

  const populateStoreWithParams = async store => {
    const { params, search } = getLocationData()

    if (!search || search === '') {
      console.error(PREVIEW_PARAM_ERROR)
      return
    }

    const paramObj = getParams(params, store) || {}
    const {
      contentfulId,
      preview: previewString,
      themeId,
      contentReview,
      isFamilyMode,
      isCurriculumPreviewMode,
      isHighSchoolMode,
      isHighSchoolPreviewMode,
      isPrepMode,
    } = paramObj

    let { locale } = paramObj

    // Get locale from URL, session storage, or default to English
    if (!locale) {
      const checkForLocaleInSessionStorage = getLocaleFromSession()
      if (checkForLocaleInSessionStorage) {
        locale = checkForLocaleInSessionStorage
      } else {
        locale = LOCALES.EN_US
      }
    }

    if (!contentfulId) {
      console.error(ENTRY_ID_PARAM_ERROR)
      return
    }

    const previewBool = getBoolFromString(previewString)

    if (previewBool === null) {
      console.error(PREVIEW_PARAM_ERROR)
      return
    }

    const hasParams = Object.keys(params).length > 0
    const hasMockData = params.has(MOCK_DATA)
    const useMockData = hasMockData || (!hasParams && !contentfulId)
    let presentationJson, entryFromCache

    if (!useMockData) {
      presentationJson = await loadPresentationJson(
        contentfulId,
        previewBool,
        locale,
        contentReview,
      )

      entryFromCache = await loadPresentationJson(
        contentfulId,
        previewBool,
        '*',
        contentReview,
      )
    }

    await store.dispatch(addTheme(themeId))
    await store.dispatch(updateCmiLocale(locale))
    store = await setPresentation(
      presentationJson,
      store,
      isPrepMode,
      locale,
      isFamilyMode,
      isCurriculumPreviewMode,
      isHighSchoolMode,
      isHighSchoolPreviewMode,
    )
    const vttByEntries = getVttsByEntries(entryFromCache)
    if (vttByEntries) {
      await store.dispatch(setVtts(vttByEntries))
    }

    return store
  }

  const getVttsByEntries = presentation => {
    try {
      const sections =
        presentation?.data?.fields?.sections[LOCALES.EN_US] || null //sections
      let vttEntryList = {}
      if (sections) {
        sections.map(section => {
          const slides = section?.fields?.slides[LOCALES.EN_US] || null //slides per Section
          if (slides) {
            slides.map(slide => {
              const fieldsLayout = slide?.fields?.layout[LOCALES.EN_US] || null
              const column = fieldsLayout?.fields?.column1 || null
              if (column && column[LOCALES.EN_US]) {
                const { videoRef } = column[LOCALES.EN_US][0]?.fields || null

                if (videoRef && videoRef[LOCALES.EN_US]) {
                  const videRefFields = videoRef[LOCALES.EN_US]?.fields || null
                  const vttFile =
                    videRefFields?.vttFile[LOCALES.EN_US][0] || null
                  if (!videRefFields && vttFile) {
                    return null
                  }

                  const vtFile = vttFile?.fields?.file || null
                  vttEntryList = { ...vttEntryList, [slide.sys.id]: vtFile }
                }
              }
            })
          }
        })
      }
      return vttEntryList
    } catch (error) {
      return null
    }
  }

  const setPresentation = async (
    presentationJson,
    store,
    isPrepMode,
    locale,
    isFamilyMode,
    isCurriculumPreviewMode,
    isHighSchoolMode,
    isHighSchoolPreviewMode,
  ) => {
    const { data: presentation, status } = presentationJson || {}
    const didFail = status !== SUCCESS
    if (didFail) {
      console.error(LOAD_PRESENTATION_ERROR)
      setError(presentationJson)
      return store
    }
    await store.dispatch(togglePrepMode(isPrepMode))
    await store.dispatch(setFamilyMode(isFamilyMode))
    await store.dispatch(setCurriculumPreviewMode(isCurriculumPreviewMode))
    await store.dispatch(setHighSchoolMode(isHighSchoolMode))
    await store.dispatch(setHighSchoolPreviewMode(isHighSchoolPreviewMode))
    await store.dispatch(initializePlayer(presentation))
    await rehydrateRedux(store, locale)

    IS_CONNECTED_TO_API && getPlayerSessionTimeManager(store)

    const { fields = {} } = presentation
    const { displayTitle = {} } = fields

    if (!IS_CONNECTED_TO_API && displayTitle) {
      document.title = displayTitle
    }

    return store
  }

  return { initialStore, error }
}
